import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { observer, useLocalObservable } from 'mobx-react-lite';
import PeriodDateFilter from '../../../component/period-date-filter.component';
import ScrollTabs from '../../../component/tab/scroll-tabs.component';
import GlobalNotification from '../../../component/common/snackbar';
import AlertDialog from '../../../component/dialog/alert-dialog.component';
import { DataExportIconButton } from '../../../component/button/data-export-icon-button.component';
import i18n from '../../../i18n';
import { MobxObjectsManager } from '../../mobx-objs/mobx-manager';
import MainPageState from '../../main/main-page-states';
import { ErrorMessageBox } from '../data-analysis.page';
import apiManage from '../../../request';
import SmartAnalysisButton from '../../common/smart-analysis-button.component';
import DishAiAnalysisDialog from './dish-ai-analysis-dialog.component';
import DishSalesCompareDialog from './dish-sales-compare-dialog';
import ExtraAnalysisCompareDialog from './extra-analysis-compare-dialog';
import DishSalesTabPage from './tabs/dish-sales-tab.page';
import EfficiencyAnalysisTabPage from './tabs/efficiency-analysis-tab.page';
import ExtraAnalysisTabPage from './tabs/extra-analysis-tab.page';
import DishDataPageState from './state/dish-data-page-state';
import DishSalesState from './state/dish-sales-state';
import ExtraAnalysisState from './state/extra-analysis-state';
import EfficiencyAnalysisState from './state/efficiency-analysis-state';
import { DataAnalysisAiSummaryRequest, RestaurantMenuSalesAnalysisType } from '../../../request/api/data-analysis';
import {
    toEfficiencyAiSummaryListItems,
    toExtraFeeAiSummaryListItems,
    toMenuSalesAiSummaryListItems,
} from './dish-ai-summary-payload.utils';
import dataAnalysisStore from '../data-analysis-state';

/**
 * 菜品分析父页面。
 */
const DishDataPage = observer(() => {
    /** 父页面协调状态。 */
    const pageState = useLocalObservable(() => new DishDataPageState());
    /** 额外费用分析状态。 */
    const extraState = useLocalObservable(() => new ExtraAnalysisState());
    /** 菜品销售分析状态。 */
    const dishState = useLocalObservable(() => new DishSalesState());
    /** 出菜率分析状态。 */
    const efficiencyState = useLocalObservable(() => new EfficiencyAnalysisState());
    /** 主页面状态。 */
    const mainPageData = MobxObjectsManager.get(MainPageState);
    /** 当前餐厅 string_id。 */
    const stringId = mainPageData.currentRestaurant?.string_id ?? '';
    /** 当前餐厅版本。 */
    const restaurantVersion = mainPageData.currentRestaurant?.restaurant_version;
    /** 营业分析全局起止时间（与其它子页共用 dataAnalysisStore）。 */
    const startDate = dataAnalysisStore.startDate;
    /** 营业分析全局结束时间。 */
    const endDate = dataAnalysisStore.endDate;
    /** AI 弹窗开关。 */
    const [aiDialogOpen, setAiDialogOpen] = React.useState(false);
    /** AI 请求参数。 */
    const [aiRequestParams, setAiRequestParams] = React.useState<DataAnalysisAiSummaryRequest | null>(null);
    /** 导出二次确认弹窗开关。 */
    const [exportConfirmOpen, setExportConfirmOpen] = React.useState(false);
    /** 当前页签配置。 */
    const tabItems = React.useMemo(() => ([
        { key: 'extra', title: i18n.t('business_analysis_page_additional_cost_sales_analysis') },
        { key: 'dish', title: i18n.t('business_analysis_page_dishes_sales_analysis') },
        { key: 'efficiency', title: i18n.t('business_analysis_page_analysis_efficiency_dishes') },
    ] as const), [i18n.language]);

    /**
     * 将日期筛选组件周期值映射为后端标准 mode。
     * @param periodMode - 页面周期值
     * @returns 后端 mode
     */
    const mapPeriodModeToApiMode = (periodMode: 'this_month' | 'this_week' | 'today' | 'custom') => {
        if (periodMode === 'this_month') {
            return 'month';
        }
        if (periodMode === 'this_week') {
            return 'week';
        }
        return periodMode;
    };

    /**
     * 餐厅切换时，仅同步各 Tab 的餐厅上下文，不在此处预取全部数据。
     */
    useEffect(() => {
        if (!stringId) {
            return;
        }
        dishState.setRestaurantStringId(stringId);
        efficiencyState.setRestaurantStringId(stringId);
    }, [dishState, efficiencyState, stringId]);

    /**
     * 额外费用页签激活后，才请求额外费用数据。
     */
    useEffect(() => {
        if (!stringId || pageState.activeTab !== 'extra') {
            return;
        }
        void extraState.fetchData(stringId);
    }, [extraState, pageState.activeTab, stringId, startDate, endDate]);

    /**
     * 菜品销售页签激活后，才请求自己的菜单筛选项。
     */
    useEffect(() => {
        if (!stringId || pageState.activeTab !== 'dish') {
            return;
        }
        void dishState.fetchFilters(stringId);
    }, [dishState, pageState.activeTab, stringId]);

    /**
     * 菜品销售页签激活后，才请求自己的列表数据。
     */
    useEffect(() => {
        if (!stringId || pageState.activeTab !== 'dish') {
            return;
        }
        void dishState.fetchData(stringId);
    }, [
        dishState,
        pageState.activeTab,
        stringId,
        startDate,
        endDate,
        dishState.analysisType,
        dishState.mainGroupId,
        dishState.secondMenuId,
    ]);

    /**
     * 出菜率页签激活后，才请求自己的菜单筛选项。
     */
    useEffect(() => {
        if (!stringId || pageState.activeTab !== 'efficiency') {
            return;
        }
        void efficiencyState.fetchFilters(stringId);
    }, [efficiencyState, pageState.activeTab, stringId]);

    /**
     * 出菜率页签激活后，才请求自己的列表数据。
     */
    useEffect(() => {
        if (!stringId || pageState.activeTab !== 'efficiency') {
            return;
        }
        void efficiencyState.fetchData(stringId);
    }, [
        efficiencyState,
        pageState.activeTab,
        stringId,
        startDate,
        endDate,
        efficiencyState.mainGroupId,
        efficiencyState.secondMenuId,
    ]);

    /**
     * 菜品销售筛选变更后关闭对比弹窗，避免残留旧上下文。
     */
    useEffect(() => {
        pageState.closeCompareDialog();
    }, [
        pageState,
        startDate,
        endDate,
        dishState.analysisType,
        dishState.mainGroupId,
        dishState.secondMenuId,
    ]);

    /**
     * 若当前已不足两项可对比，则自动关闭对比弹窗。
     */
    useEffect(() => {
        if (pageState.compareDialogTab !== 'dish' || dishState.canCompare) {
            return;
        }
        pageState.closeCompareDialog();
    }, [pageState, pageState.compareDialogTab, dishState.canCompare]);

    /**
     * 若当前额外费用分析已不足两项可对比，则自动关闭额外费用对比弹窗。
     */
    useEffect(() => {
        if (pageState.compareDialogTab !== 'extra' || extraState.canCompare) {
            return;
        }
        pageState.closeCompareDialog();
    }, [pageState, pageState.compareDialogTab, extraState.canCompare]);

    /**
     * 切换到非菜品销售页签时关闭对比弹窗。
     */
    useEffect(() => {
        if (!pageState.compareDialogTab || pageState.compareDialogTab === pageState.activeTab) {
            return;
        }
        pageState.closeCompareDialog();
    }, [pageState, pageState.activeTab, pageState.compareDialogTab]);

    /**
     * 额外费用筛选变更后关闭对比弹窗，避免残留旧上下文。
     */
    useEffect(() => {
        if (pageState.compareDialogTab !== 'extra') {
            return;
        }
        pageState.closeCompareDialog();
    }, [
        pageState,
        startDate,
        endDate,
        extraState.searchKeyword,
    ]);

    /**
     * 渲染当前页签内容。
     * @returns 页签 JSX
     */
    const renderActiveTab = () => {
        if (pageState.activeTab === 'extra') {
            return (
                <ExtraAnalysisTabPage
                    store={extraState}
                    onOpenCompareDialog={() => pageState.openCompareDialog('extra')}
                />
            );
        }
        if (pageState.activeTab === 'dish') {
            return (
                <DishSalesTabPage
                    store={dishState}
                    onOpenCompareDialog={() => pageState.openCompareDialog('dish')}
                />
            );
        }
        return (
            <EfficiencyAnalysisTabPage
                store={efficiencyState}
            />
        );
    };

    /**
     * 构建额外费用分析 AI 请求参数（payload 含 list，且 list 项不含 daily_curve）。
     * @returns AI 请求参数
     */
    const buildExtraAiRequest = (): DataAnalysisAiSummaryRequest | null => {
        if (!stringId) {
            return null;
        }
        if (extraState.filteredRows.length === 0) {
            return null;
        }
        /** 供 AI 使用的列表（去掉按日曲线等大字段）。 */
        const list = toExtraFeeAiSummaryListItems(extraState.filteredRows);
        return {
            string_id: stringId,
            type: 'service_extra_fee',
            payload: {
                mode: mapPeriodModeToApiMode(dataAnalysisStore.getPeriodMode()),
                time_range: {
                    start: dataAnalysisStore.startDate,
                    end: dataAnalysisStore.endDate,
                },
                list,
            },
        };
    };

    /**
     * 构建菜品销售分析 AI 请求参数（payload 含 list，且 list 项不含 quantity_curve）。
     * @returns AI 请求参数
     */
    const buildDishAiRequest = (): DataAnalysisAiSummaryRequest | null => {
        if (!stringId) {
            return null;
        }
        if (dishState.filteredRows.length === 0) {
            return null;
        }
        /** 菜品销售维度。 */
        const salesAnalysisType = dishState.analysisType === 'menu'
            ? RestaurantMenuSalesAnalysisType.Menu
            : dishState.analysisType === 'category'
                ? RestaurantMenuSalesAnalysisType.Category
                : RestaurantMenuSalesAnalysisType.Item;
        /** 供 AI 使用的列表（去掉数量趋势线等大字段）。 */
        const list = toMenuSalesAiSummaryListItems(dishState.filteredRows);
        return {
            string_id: stringId,
            type: 'menu_sales',
            menu_sales_analysis_type: salesAnalysisType,
            payload: {
                mode: mapPeriodModeToApiMode(dataAnalysisStore.getPeriodMode()),
                time_range: {
                    start: dataAnalysisStore.startDate,
                    end: dataAnalysisStore.endDate,
                },
                list,
            },
        };
    };

    /**
     * 构建出菜率分析 AI 请求参数（payload 含 list）。
     * @returns AI 请求参数
     */
    const buildEfficiencyAiRequest = (): DataAnalysisAiSummaryRequest | null => {
        if (!stringId) {
            return null;
        }
        if (efficiencyState.filteredRows.length === 0) {
            return null;
        }
        /** 供 AI 使用的列表（去掉前端辅助字段）。 */
        const list = toEfficiencyAiSummaryListItems(efficiencyState.filteredRows);
        return {
            string_id: stringId,
            type: 'dish_efficiency',
            payload: {
                mode: mapPeriodModeToApiMode(dataAnalysisStore.getPeriodMode()),
                time_range: {
                    start: dataAnalysisStore.startDate,
                    end: dataAnalysisStore.endDate,
                },
                list,
            },
        };
    };

    /**
     * 按当前页签生成 AI 请求参数。
     * @returns AI 请求参数
     */
    const buildAiRequestByActiveTab = (): DataAnalysisAiSummaryRequest | null => {
        if (pageState.activeTab === 'extra') {
            return buildExtraAiRequest();
        }
        if (pageState.activeTab === 'dish') {
            return buildDishAiRequest();
        }
        return buildEfficiencyAiRequest();
    };

    /** 当前页签参与导出的行数。 */
    const exportRowCount = useMemo(() => {
        if (pageState.activeTab === 'extra') {
            return extraState.rowsForExport.length;
        }
        if (pageState.activeTab === 'dish') {
            return dishState.rowsForExport.length;
        }
        return efficiencyState.rowsForExport.length;
    }, [
        pageState.activeTab,
        extraState.rowsForExport.length,
        dishState.rowsForExport.length,
        efficiencyState.rowsForExport.length,
    ]);

    /** 当前页签是否按勾选行导出。 */
    const exportUsesSelectedRows = useMemo(() => {
        if (pageState.activeTab === 'extra') {
            return extraState.exportUsesSelectedRows;
        }
        if (pageState.activeTab === 'dish') {
            return dishState.exportUsesSelectedRows;
        }
        return efficiencyState.exportUsesSelectedRows;
    }, [
        pageState.activeTab,
        extraState.exportUsesSelectedRows,
        dishState.exportUsesSelectedRows,
        efficiencyState.exportUsesSelectedRows,
    ]);

    /** 导出确认弹窗强调文案（已勾选条数或全部条数）。 */
    const exportConfirmHighlight = useMemo(() => {
        if (exportUsesSelectedRows) {
            return i18n.t('business_analysis_page_export_confirm_selected_count', { count: exportRowCount });
        }
        return i18n.t('business_analysis_page_export_confirm_all_count', { count: exportRowCount });
    }, [exportRowCount, exportUsesSelectedRows, i18n.language]);

    /**
     * 导出前预检：当前页签无可导出行则提示并中止。
     * @returns 是否可继续打开确认弹窗
     */
    const preCheckExport = useCallback((): boolean => {
        if (exportRowCount === 0) {
            GlobalNotification.warning(i18n.t('general_text_no_data'));
            return false;
        }
        return true;
    }, [exportRowCount]);

    /**
     * 点击导出图标：通过预检后打开二次确认弹窗。
     */
    const handleExportClick = useCallback(() => {
        setExportConfirmOpen(true);
    }, []);

    /**
     * 确认导出：按当前页签状态导出勾选行或筛选后全部行。
     */
    const handleExportConfirm = useCallback(() => {
        if (pageState.activeTab === 'extra') {
            extraState.exportData();
        } else if (pageState.activeTab === 'dish') {
            dishState.exportData();
        } else {
            efficiencyState.exportData();
        }
        setExportConfirmOpen(false);
    }, [dishState, efficiencyState, extraState, pageState.activeTab]);

    /**
     * 当前页签列表是否在加载（导出按钮禁用）。
     */
    const exportDisabled = useMemo(() => {
        if (pageState.activeTab === 'extra') {
            return extraState.loading;
        }
        if (pageState.activeTab === 'dish') {
            return dishState.loading;
        }
        return efficiencyState.loading;
    }, [dishState.loading, efficiencyState.loading, extraState.loading, pageState.activeTab]);

    /**
     * 点击智能分析按钮。
     */
    const handleSmartAnalysisClick = async () => {
        if (!stringId) {
            GlobalNotification.error(i18n.t('general_text_no_data'));
            return;
        }
        /** 餐厅授权校验结果。 */
        const warrantResponse = await apiManage.check_warrant_code({ string_id: stringId, code: 'AU1030' });
        if (warrantResponse?.error || !warrantResponse.passedWarrant?.length) {
            GlobalNotification.error(i18n.t('restaurant_warrant_page_text_no_auth'));
            return;
        }
        /** 当前页签请求参数。 */
        const nextRequestParams = buildAiRequestByActiveTab();
        if (!nextRequestParams) {
            GlobalNotification.info(i18n.t('general_text_no_data'));
            return;
        }
        setAiRequestParams(nextRequestParams);
        setAiDialogOpen(true);
    };

    if (!restaurantVersion) {
        return (
            <ErrorMessageBox
                errorMessage={
                    mainPageData.belongedRestaurant.length === 0
                        ? i18n.t('business_analysis_page_none_restaurant')
                        : i18n.t('business_analysis_page_please_select_restaurant_version_first')
                }
            />
        );
    }

    return (
        <>
            <Stack spacing={3} sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <ScrollTabs
                        data={tabItems.map((item) => ({ title: item.title }))}
                        activeIndex={pageState.activeTabIndex}
                        onClick={(index) => pageState.setActiveTabByIndex(index)}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <DataExportIconButton
                            title={i18n.t('tunnel_management_page_export')}
                            disabled={exportDisabled}
                            preCheck={preCheckExport}
                            onExport={handleExportClick}
                        />
                        <PeriodDateFilter
                            startDate={dataAnalysisStore.startDate}
                            endDate={dataAnalysisStore.endDate}
                            pickerValue={dataAnalysisStore.pickerValue}
                            onDateTimeChange={dataAnalysisStore.handleDateTime}
                        />
                        <SmartAnalysisButton onClick={() => { void handleSmartAnalysisClick(); }} />
                    </Box>
                </Box>
                {renderActiveTab()}
            </Stack>

            <DishSalesCompareDialog
                open={pageState.compareDialogTab === 'dish'}
                onClose={() => pageState.closeCompareDialog()}
                rows={dishState.selectedRows}
            />
            <ExtraAnalysisCompareDialog
                open={pageState.compareDialogTab === 'extra'}
                onClose={() => pageState.closeCompareDialog()}
                rows={extraState.selectedRows}
            />
            <DishAiAnalysisDialog
                open={aiDialogOpen}
                onClose={() => setAiDialogOpen(false)}
                requestParams={aiRequestParams}
            />
            <AlertDialog
                open={exportConfirmOpen}
                title={i18n.t('tunnel_management_page_export')}
                bodyPrompt={i18n.t('business_analysis_page_export_confirm_prompt')}
                highlightText={exportConfirmHighlight}
                onCancel={() => setExportConfirmOpen(false)}
                onClose={() => setExportConfirmOpen(false)}
                onConfirm={handleExportConfirm}
            />
        </>
    );
});

export default DishDataPage;
